import type { AssetFile } from "./types";

/** Truncate filename if too long */
function truncateFilename(name: string, maxLength = 100) {
  if (name.length <= maxLength) {
    return name;
  }
  const extIndex = name.lastIndexOf(".");
  if (extIndex > 0 && extIndex < name.length - 1) {
    const extension = name.slice(extIndex);
    const baseName = name.slice(0, maxLength - extension.length);
    return baseName + extension;
  }
  return name.slice(0, maxLength);
}

/** Upload a single file to a directory (pure function - returns blobUrl) */
export async function uploadAsset(directory: FileSystemDirectoryHandle, file: File) {
  const assetId = crypto.randomUUID();

  // Write binary to OPFS
  const fileHandle = await directory.getFileHandle(assetId, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(file);
  await writable.close();

  // Create asset metadata
  const asset: AssetFile = {
    id: assetId,
    name: truncateFilename(file.name),
    mimeType: file.type || "application/octet-stream",
    size: file.size,
  };

  // Create Blob URL
  const blob = new Blob([file], { type: asset.mimeType });
  const blobUrl = URL.createObjectURL(blob);

  return { asset, blobUrl };
}

/** Load assets from a directory (pure function - returns url map) */
export async function loadAssetUrls(directory: FileSystemDirectoryHandle, assets: AssetFile[]) {
  const urls: Record<string, string> = {};
  const missing: string[] = [];

  for (const asset of assets) {
    try {
      const fileHandle = await directory.getFileHandle(asset.id);
      const file = await fileHandle.getFile();
      const blobUrl = URL.createObjectURL(file);
      urls[asset.id] = blobUrl;
    } catch {
      // File not found in OPFS, fallback to remote URL if available
      if (asset.url) {
        urls[asset.id] = asset.url;
      } else {
        missing.push(asset.id);
      }
    }
  }

  return { urls, missing };
}
